import axios from "axios";
import { Geometry } from "geojson";
import { circle, geoJSON } from "leaflet";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "react-query";
import { useParams } from "react-router-dom";
import {
  MeasureConfigurationPost,
  MeasureConfigurationPropertyValue,
} from "types/backend";
import {
  FormMeasureProperty,
  Measure,
  MeasurePropertyValue,
  MeasureConfLayer,
  MeasureConf,
  MeasureConfBackend,
} from "types/types";
import { customDelete, customPost } from "utils/AxiosUtils";
import { deserializeErrors } from "utils/ErrorUtils";
import { getGeojsonFeature } from "utils/GeomanUtils";

import { deserializeMeasure } from "./measureHooks";
import { round } from "lodash-es";

export function useMeasureConfigs(
  uuid: string,
  options?: Pick<UseQueryOptions, "enabled">
) {
  async function fetch(): Promise<MeasureConf[]> {
    const { data } = await axios.get<MeasureConfBackend[]>(
      `/api/studyareas/${uuid}/measureconfigurations`
    );

    return deserializeMeasureConfigs(data);
  }
  return useQuery(["measureConfigurations", uuid], fetch, {
    ...options,
    staleTime: Infinity,
  });
}

export function useMeasureConfigsByMeasureType(measureConfigs: MeasureConf[]) {
  const hardenedSurfaceConfigs = measureConfigs.filter(
    (measureConfig) => measureConfig.measure.hardenedSurface === true
  );
  const adaptiveConfigs = measureConfigs.filter(
    (measureConfig) => measureConfig.measure.hardenedSurface === false
  );
  return { hardenedSurfaceConfigs, adaptiveConfigs };
}

function deserializeMeasureConfigs(
  backendMeasureConfiguration: MeasureConfBackend[]
): MeasureConf[] {
  return backendMeasureConfiguration.map((measureConfig) => {
    const { geometryList_4326, measure, propertyValueList, ...rest } =
      measureConfig;

    const deserializedMeasure = deserializeMeasure(measure);
    const deserializedProperties = deserializeProperties(propertyValueList);

    const diameter = deserializedProperties.find(
      (prop) => prop.propertyKey === "diameter"
    )?.value;

    const convertedGeometries = geometryList_4326.map((geometry) => {
      const { geometry_4326, ...rest } = geometry;
      const convertedGeometry = convertGeometryToGeoJSON(
        geometry_4326,
        measure.geometryType,
        diameter
      );
      return { ...rest, geometryLayer: convertedGeometry };
    });

    return {
      ...rest,
      layers: convertedGeometries,
      measure: deserializedMeasure,
      propertyValueList: deserializedProperties,
    };
  });
}

function deserializeProperties(
  propertyValueList: MeasurePropertyValue[]
): FormMeasureProperty[] {
  return propertyValueList.map((property) => {
    return {
      //FIXME: Hier pikke de kiekes
      ...property.measurePropertyLink,
      value: round(property.value, property.measurePropertyLink.nbDigits),
    };
  });
}

export function convertGeometryToGeoJSON(
  geometry: Geometry,
  geometryType: string,
  diameter: number = 1
) {
  if (geometryType === "POINT") {
    return geoJSON(geometry, {
      pointToLayer: function (feature, latLng) {
        return circle(latLng, diameter / 2);
      },
    });
  }
  return geoJSON(geometry);
}

export function useSelectedMeasureConfiguration(
  measureConfigs: MeasureConf[],
  selectedMeasure?: Measure
): MeasureConf | undefined {
  const { groupId } = useParams();
  if (!selectedMeasure) return;
  const foundMeasureConfig = measureConfigs.find(
    (measureConfig) => measureConfig.id === parseInt(groupId || "0")
  );
  if (foundMeasureConfig) return foundMeasureConfig;

  return {
    id: Date.now() + Math.random(),
    measure: selectedMeasure,
    layers: [],
    propertyValueList: [],
  };
}

export function useUpdateMeasureConfigs(uuid: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  async function post(measureConfigs: MeasureConf[]) {
    const serializedMeasureConfigs = serializeMeasureConfigs(measureConfigs);
    const { data } = await customPost<MeasureConfBackend[]>(
      `/api/studyareas/${uuid}/measureconfigurations`,
      serializedMeasureConfigs
    );
    return deserializeMeasureConfigs(data);
  }
  return useMutation(["measureConfigurations", "post", uuid], post, {
    onMutate: async (measureConfigs) => {
      await queryClient.cancelQueries(["measureConfigurations", uuid]);
      const previousMeasureConfigs = queryClient.getQueryData([
        "measureConfigurations",
        uuid,
      ]);
      queryClient.setQueryData(["measureConfigurations", uuid], measureConfigs);

      return { previousMeasureConfigs };
    },
    onError: (err) => {
      queryClient.invalidateQueries(["measureConfigurations", uuid]);
      const errors = deserializeErrors(err);
      errors.map((er) => {
        return enqueueSnackbar(er.message, {
          variant: "error",
        });
      });
    },
    onSuccess: (measureConfigs: MeasureConf[]) => {
      queryClient.setQueryData(["measureConfigurations", uuid], measureConfigs);
    },
  });
}

export function useDeleteMeasureConfiguration(uuid: string) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  async function deleteMeasureConf(measureConfigId: number) {
    await customDelete(
      `/api/studyareas/${uuid}/measureconfigurations/${measureConfigId}`
    );
  }

  return useMutation(deleteMeasureConf, {
    onMutate: async (measureConfigId) => {
      await queryClient.cancelQueries(["measureConfigurations", uuid]);
      const previousMeasureConfigs = queryClient.getQueryData([
        "measureConfigurations",
        uuid,
      ]);
      queryClient.setQueryData(
        ["measureConfigurations", uuid],
        (old: MeasureConf[] | undefined) =>
          (old || []).filter(
            (measureConfig: MeasureConf) => measureConfig.id !== measureConfigId
          )
      );
      return { previousMeasureConfigs };
    },
    onError: (err, n, context) => {
      if (!context)
        return queryClient.invalidateQueries(["measureConfigurations", uuid]);
      else
        queryClient.setQueryData(
          ["measureConfigurations", uuid],
          context.previousMeasureConfigs
        );
      const errors = deserializeErrors(err);
      errors.map((er) => {
        return enqueueSnackbar(er.message, {
          variant: "error",
        });
      });
    },
    onSuccess: () => {
      enqueueSnackbar(t("label.measureDeleted"), { variant: "success" });
    },
  });
}

export function serializeMeasureConfigs(
  measureConfigs: MeasureConf[]
): MeasureConfigurationPost[] {
  return measureConfigs.map((measureConfig) => {
    const { layers, propertyValueList, measure } = measureConfig;
    const geometries = layers.map(formatLayerToGeometry);

    const measureConfigProps = propertyValueList.map(
      formatFormPropToMeasureConfigProp
    );
    return {
      measureId: measure.id,
      geometryList_4326: geometries,
      propertyValueInputList: measureConfigProps,
    };
  });
}

function formatLayerToGeometry(layer: MeasureConfLayer) {
  return {
    id: layer.id,
    geometry_4326: getGeojsonFeature(layer.geometryLayer).geometry,
  };
}

function formatFormPropToMeasureConfigProp(
  measureProp: FormMeasureProperty
): MeasureConfigurationPropertyValue {
  return {
    measurePropertyLinkId: measureProp.id,
    value: measureProp.value,
  };
}

interface UploadMeasuresResponseBackend {
  measureConfigurationList_studyArea: MeasureConfBackend[];
  measureConfigurationList_uploaded: MeasureConfBackend[];
  warningList_studyArea: string[];
  warningList_uploaded: string[];
}

interface UploadMeasureResponse {
  newMeasureConfigs: MeasureConf[];
  updatedMeasureConfigs: MeasureConf[];
  studyAreaWarnings: string[];
  measuresWarnings: string[];
}

export function useUploadMeasures(uuid: string, selectedMeasureKey: string) {
  async function post(file: File) {
    const { data } = await customPost<UploadMeasuresResponseBackend>(
      `/api/studyareas/${uuid}/measureconfigurations/${selectedMeasureKey}/uploadShapeFile`,
      file,
      {
        headers: {
          "Content-Type": file.type,
        },
      }
    );

    const deserializedResponse: UploadMeasureResponse = {
      newMeasureConfigs: deserializeMeasureConfigs(
        data.measureConfigurationList_uploaded
      ),
      updatedMeasureConfigs: deserializeMeasureConfigs(
        data.measureConfigurationList_studyArea
      ),
      studyAreaWarnings: data.warningList_studyArea,
      measuresWarnings: data.warningList_uploaded,
    };
    return deserializedResponse;
  }
  return useMutation(["measures", "upload"], post, {});
}
